import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalDirective } from 'ngx-bootstrap';
import { Observable } from 'rxjs/Observable';
import { EnvironmentConfiguration } from '../../../environment/services/environment.configuration';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { OnyxEvent } from '../../models/event';
import { RsvpUploadResponse } from '../../models/rsvp-upload-response';
import { RsvpStore } from '../../service/rsvp.store';
import { ReservationCompareStore } from '../../service/reservation-compare-store';

@Component({
  selector: 'pioneer-rsvp-bulk',
  templateUrl: './rsvp-bulk.component.html',
  styleUrls: [ './rsvp-bulk.component.css' ]
})
export class RsvpBulkComponent implements OnInit {

  @ViewChild(ModalDirective)
  public rsvpBulkModal: ModalDirective;

  onyxEvent: OnyxEvent;

  fileExtensions: Array<string>;

  acceptedFileTypes: Array<string>;

  isUploadInProgress: boolean;
  isBeforeUpload = true;
  isSuccessful: boolean;

  isAllowedToClosePopup = true;
  public templateUrl: string;

  totalRecords = 0;
  processedRecords = 0;
  failedRecords = 0;
  rejectedFileUrl: string;

  @ViewChild('file') fileInput: ElementRef;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  @Input()
  public config = {
    backdrop: 'static',
    keyboard: false
  };

  @Output()
  public closed = new EventEmitter();

  constructor(private flashMessagesService: FlashMessagesService,
              private environment: EnvironmentConfiguration,
              private rsvpStore: RsvpStore,
              private authenticationStore: AuthenticationStore,
              private errorHandlerService: ErrorHandlerService,
              private translateService: TranslateService,
              private reservationCompareStore: ReservationCompareStore) { }

  ngOnInit() {
    if (!this.onyxEvent) {
      throw new Error('The "event" attribute must be specified to use the RsvpBulk component');
    }

    this.fileExtensions = this.environment.rsvps.acceptedFileExtensions;
    this.acceptedFileTypes = this.environment.rsvps.acceptedFileTypes;
    this.templateUrl = this.environment.baseUrl
      + '/api/events/rsvps/import/template?Authorization='
      + this.authenticationStore.getToken();
    this.isAllowedToClosePopup = true;
  }

  public open(): void {
    this.rsvpBulkModal.show();
  }

  public close(value: boolean): void {
    this.closed.emit(value);
    this.rsvpBulkModal.hide();
  }

  public cancel(): void {
    this.close(false);
    this.reset();
  }

  public reset(): void {
    this.isUploadInProgress = false;
    this.isBeforeUpload = true;
    this.isSuccessful = false;

    this.processedRecords = 0;
    this.totalRecords = 0;
    this.failedRecords = 0;
    this.rejectedFileUrl = null;
    this.isAllowedToClosePopup = true;
  }

  public importRsvpFile(inputEvent) {
    this.isUploadInProgress = true;

    if (!inputEvent || !inputEvent.srcElement || !inputEvent.srcElement.files || inputEvent.srcElement.files.size === 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_BULK_RSVP');
      return;
    }

    const file = inputEvent.srcElement.files[0];
    this.clearFileInput();

    if (this.acceptedFileTypes.indexOf(file.type) < 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_BULK_RSVP');
      return;
    }

    const eventId = this.onyxEvent.eventId;

    this.rsvpStore.importRsvpFile(eventId, file)
      .finally(() => {
        this.isBeforeUpload = false;
        this.isUploadInProgress = false;
        if (this.isSuccessful && this.processedRecords === 0) {
          this.handleNothingProcessed();
          return;
        }
      })
      .catch(error => {
        this.isSuccessful = false;
        this.isAllowedToClosePopup = false;
        this.handleUploadError(this.errorHandlerService.getErrorMessageFromBackendError(error),
          `Error uploading rsvps file: ${error}`);
        return Observable.throw(error);
      })
      .subscribe((response: RsvpUploadResponse) => {
        this.isSuccessful = response.failedRecords === 0;
        this.totalRecords = response.totalRecords;
        this.processedRecords = response.processedRecords;
        this.failedRecords = response.failedRecords;
        this.rejectedFileUrl = response.rejectedFileUrl;
        if (!this.isSuccessful) {
          this.isAllowedToClosePopup = false;
        }
        this.reservationCompareStore.refreshCurrentRsvpList(this.onyxEvent.eventId);
      });
  }

  private handleUploadError(errorMessage: string, logMessage: string = null) {
    this.isUploadInProgress = false;
    this.flashMessagesService.error(errorMessage, logMessage);
  }

  private handleNothingProcessed() {
    this.isBeforeUpload = true;
    this.flashMessagesService.error(this.translateService.instant('EVENT.RSVPS.UPLOAD_BULK_RESERVATION.EMPTY_FILE'));
  }

  /**
   * This needs to be called whenever we do an upload so that someone could do a second upload with a different file
   * without having to reload the page.
   */
  private clearFileInput(): void {
    this.fileInput.nativeElement.value = null;
  }

  public fileDownloaded(): void {
    this.isAllowedToClosePopup = true;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/rsvp/rsvp-bulk/rsvp-bulk.component.ts